<script setup lang="ts">
import type { RouteLocationRaw } from 'vue-router'
import type { JournalArticle, JournalListItem } from '~/utils/solr/journal'
import { FavoriteButton, NuxtLink } from '#components'
import { faLockKeyhole } from '@fortawesome/pro-light-svg-icons'

const props = defineProps<{
  publisherId: JournalListItem['publisherId']
  issueNumber: JournalListItem['issue']
  volume: JournalListItem['volume']
  article: JournalArticle
}>()

const { isFavorite, toggleFavorite } = useFavorites()

const url = computed(() => ({
  name: 'journal-article-volume',
  params: {
    publisherId: props.publisherId || '',
    volume: props.volume || '',
    issue: props.issueNumber || '',
    articleNumber: props.article.id.split('/').pop() || props.article.id,
  },
}) satisfies RouteLocationRaw)

const hasLicense = useHasContentLicense()

const publishDateLabel = computed(() => {
  if (!props.article.publish_date) {
    return null
  }

  return formatDate(props.article.publish_date, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
})
</script>

<template>
  <NuxtLink
    :to="url"
    class="block w-full rounded-lg border-2 border-transparent p-s text-left no-underline transition-colors duration-200 ease-out hover:bg-surface-secondary hover:border-surface-secondary active:bg-blurple-200 active:border-blurple-200"
    :aria-label="`Les artikkelen ${article.title}`"
  >
    <div class="flex items-center justify-between mb-3xs">
      <h4 class="font-semibold text-sm md:text-lg max-w-[550px]">
        {{ article.title }}
      </h4>
      <FavoriteButton
        v-if="hasLicense"
        intent="outlined"
        class="flex-shrink-0"
        :selected="isFavorite('journals', article.id)"
        :aria-label="isFavorite('journals', article.id) ? `Fjern ${article.title} fra favoritter` : `Legg til ${article.title} i favoritter`"
        @click.stop.prevent="toggleFavorite('journals', article.id)"
      />
      <Icon v-if="!hasLicense" :icon="faLockKeyhole" class="flex-shrink-0 text-carbon-500" />
    </div>
    <div class="space-y-xs">
      <h5 v-if="article.author?.length && article.author.length > 0" class="font-normal text-xsmall text-carbon-600 max-w-[550px]">
        {{ article.author.join(', ') }}
      </h5>
      <div v-if="(article.firstPage && article.lastPage) || publishDateLabel" class="flex items-center gap-2xs text-caption text-carbon-400">
        <template v-if="article.firstPage && article.lastPage">
          s. {{ article.firstPage }}-{{ article.lastPage }}
        </template>
        <template v-if="publishDateLabel">
          <span v-if="article.firstPage && article.lastPage" aria-hidden="true" class="text-sm text-blurple-300 font-light">|</span>
          {{ publishDateLabel }}
        </template>
      </div>
    </div>
  </NuxtLink>
</template>
